// ============================================================================
// ECHART — SHARED WRAPPER
// ============================================================================
// Thin React wrapper around echarts-for-react that:
//   1. Uses the tree-shaken echarts instance (echart-themes.ts)
//   2. Picks 'prima-light' / 'prima-dark' theme based on <html data-theme>
//   3. Handles responsive resize
//   4. Forwards chart events (click, etc.) with a consistent signature
//   5. Shows an optional loading overlay without tearing down the chart
//
// Widgets pass an `option` object; the wrapper handles everything else.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import type { ECharts, EChartsCoreOption } from 'echarts/core';
import { echarts } from './echart-themes';

export interface EChartProps {
  option: EChartsCoreOption;
  height?: number | string;
  loading?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** Chart events (e.g. { click: (params, instance) => ... }) for drill-down. */
  onEvents?: Record<string, (params: any, instance: ECharts) => void>;
  /** Fires once the chart instance is available. */
  onReady?: (instance: ECharts) => void;
  /** When true, a new option fully replaces the previous one (default false). */
  notMerge?: boolean;
}

// ---------------------------------------------------------------------------
// Dark-mode subscription
// ---------------------------------------------------------------------------

function getTheme(): 'prima-light' | 'prima-dark' {
  if (typeof document === 'undefined') return 'prima-light';
  return document.documentElement.getAttribute('data-theme') === 'dark'
    ? 'prima-dark'
    : 'prima-light';
}

function useChartTheme(): 'prima-light' | 'prima-dark' {
  const [theme, setTheme] = useState<'prima-light' | 'prima-dark'>(getTheme());

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const observer = new MutationObserver(() => {
      const next = getTheme();
      setTheme((prev) => (prev === next ? prev : next));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  return theme;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const EChart: React.FC<EChartProps> = ({
  option,
  height = 260,
  loading = false,
  className,
  style,
  onEvents,
  onReady,
  notMerge = false,
}) => {
  const theme = useChartTheme();
  const instanceRef = useRef<ECharts | null>(null);

  const handleChartReady = useCallback(
    (instance: any) => {
      instanceRef.current = instance as ECharts;
      if (onReady) onReady(instance as ECharts);
    },
    [onReady],
  );

  // Force a remount when the theme changes so the new theme takes effect.
  // (echarts-for-react only applies the theme at init time.)
  const chartKey = `chart-${theme}`;

  return (
    <div
      className={className}
      style={{ position: 'relative', width: '100%', height, ...style }}
    >
      <ReactEChartsCore
        key={chartKey}
        echarts={echarts}
        option={option}
        theme={theme}
        notMerge={notMerge}
        lazyUpdate
        style={{ width: '100%', height: '100%' }}
        opts={{ renderer: 'canvas' }}
        showLoading={loading}
        loadingOption={{
          text: '',
          color: theme === 'prima-dark' ? '#60a5fa' : '#3b82f6',
          textColor: 'transparent',
          maskColor: 'transparent',
          zlevel: 0,
        }}
        onChartReady={handleChartReady}
        onEvents={onEvents as any}
      />
    </div>
  );
};

export default EChart;
